import { Id } from '../../../types/definitions'
import { AccountMergeRequestStore } from '../AccountMergeRequestStore'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { AccountMergeRequestItemViewModel } from '../AccountMergeRequestItemViewModel'
import { AlertFactory, AlertState } from '../../../alerts/Alert'

export class IgnoreMergeRequestDialogViewModel {
  /**
   * Whether the dialog should be shown.
   */
  @observable
  showing = false

  /**
   * The account merge request to ignore.
   */
  @observable
  private _accountMergeRequest: AccountMergeRequestItemViewModel | null = null

  constructor(
    private readonly _store: AccountMergeRequestStore,
    private readonly _refreshItems: () => Promise<void>,
    private readonly _newAlert: AlertFactory,
  ) {
    makeObservable(this)
  }

  /**
   * The customer's display name.
   */
  @computed
  get customerDisplayName() {
    return (
      this._accountMergeRequest?.displayName ??
      this._accountMergeRequest?.email ??
      '(Not Provided)'
    )
  }

  /**
   * Inflates the dialog for the given account merge request.
   */
  @action.bound
  show(accountMergeRequest: AccountMergeRequestItemViewModel) {
    this._accountMergeRequest = accountMergeRequest
    this.showing = true
  }

  /**
   * Closes the dialog.
   */
  @action.bound
  close() {
    this.showing = false
  }

  /**
   * Ignores the merge request.
   */
  ignore = task.resolved(() => {
    if (!this._accountMergeRequest) {
      return
    }

    return this._store
      .ignore(Id.assert(this._accountMergeRequest.id))
      .then(this._refreshItems)
      .then(this.close)
      .then(() =>
        this._newAlert()
          .saying('The account merge request was successfully ignored.')
          .withState(AlertState.Success)
          .autoDismiss()
          .show(),
      )
  })
}
